/**
 * 我的山海 — 統一架構主應用
 *
 * 單一 Three.js/WebGPU Canvas + 離屏侵蝕引擎
 * 底部工具欄：所有工具並排展示，無分頁
 */

import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { WebGPURenderer } from 'three/webgpu'
import * as THREE from 'three/webgpu'
import { useGameStore, type PlacedObjectType, type TerrainTool, type ToolGroup, type MapStyle } from '../core/store/gameStore'
import { TerrainMesh, WaterPlane } from '../components/TerrainMesh'
import { TerrainMeshQingLv, WaterPlaneQingLv } from '../components/TerrainMeshQingLv'
import { ProceduralSkyShuimo, TerrainMeshShuimo, WaterPlaneShuimo } from '../components/TerrainMeshShuimo'
import { ProceduralSky } from '../components/ProceduralSky'
import { ProceduralSkyQingLv } from '../components/ProceduralSkyQingLv'
import { FirstPersonController } from '../components/FirstPersonController'
import EditController from '../components/EditController'
import PlacedObjects from '../components/PlacedObjects'
import MapLabels from '../components/MapLabels'
import TerrainEditor from '../components/TerrainEditor'
import { Inhabitants, DraftPreview } from '../components/Inhabitants'
import { PlaceBeastController } from '../components/PlaceBeastController'
import { BeastStudio } from '../components/BeastStudio'
import { StudioStage } from '../components/StudioStage'
import { PlantMaker } from '../components/PlantMaker'
import { SkyFlock } from '../components/SkyFlock'
import { ErosionEngine } from '../core/ErosionEngine'
import { useWorldPersist, collectSave, hydrateWorldSave } from '../content/useWorldPersist'
import { downloadWorldPack, parseWorldPack, writeWorldSave } from '../content/worldSave'
import { HEIGHTMAP_SIZE } from '../core/terrainConstants'
import { terrainBuffer } from '../core/terrainBuffer'

// 預分配向量（避免 useFrame 內每幀 new）
const _panDir = new THREE.Vector3()
const _panRight = new THREE.Vector3()
const _panForward = new THREE.Vector3()

/** 編輯模式鍵盤平移（方向鍵 + WASD），解決觸控板無中鍵平移的問題 */
function CameraKeyPan() {
  const keys = useRef({ up: false, down: false, left: false, right: false })

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      const k = e.key
      if (k === 'ArrowUp' || k === 'w' || k === 'W') keys.current.up = true
      else if (k === 'ArrowDown' || k === 's' || k === 'S') keys.current.down = true
      else if (k === 'ArrowLeft' || k === 'a' || k === 'A') keys.current.left = true
      else if (k === 'ArrowRight' || k === 'd' || k === 'D') keys.current.right = true
    }
    const onUp = (e: KeyboardEvent) => {
      const k = e.key
      if (k === 'ArrowUp' || k === 'w' || k === 'W') keys.current.up = false
      else if (k === 'ArrowDown' || k === 's' || k === 'S') keys.current.down = false
      else if (k === 'ArrowLeft' || k === 'a' || k === 'A') keys.current.left = false
      else if (k === 'ArrowRight' || k === 'd' || k === 'D') keys.current.right = false
    }
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp) }
  }, [])

  useFrame(({ camera, controls }, delta) => {
    const k = keys.current
    if (!k.up && !k.down && !k.left && !k.right) return
    const orbit = controls as any
    if (!orbit?.target) return

    _panRight.setFromMatrixColumn(camera.matrixWorld, 0).setComponent(1, 0).normalize()
    _panForward.setFromMatrixColumn(camera.matrixWorld, 2).negate().setComponent(1, 0).normalize()

    _panDir.set(0, 0, 0)
    const speed = 150 * delta
    if (k.up) _panDir.add(_panForward)
    if (k.down) _panDir.sub(_panForward)
    if (k.right) _panDir.add(_panRight)
    if (k.left) _panDir.sub(_panRight)

    if (_panDir.lengthSq() > 0) {
      _panDir.normalize().multiplyScalar(speed)
      orbit.target.add(_panDir)
      camera.position.add(_panDir)
    }
  })

  return null
}

/** 根據風格動態更新場景配色（霧、clear color、光照） */
function SceneConfig() {
  const mapStyle = useGameStore(s => s.mapStyle)
  const { gl, scene } = useThree()
  const isQingLv = mapStyle === 'qinglv'
  const isShuimo = mapStyle === 'shuimo'
  const isPainted = mapStyle !== 'realistic'

  useEffect(() => {
    const clearColor = isShuimo ? '#efe8dc' : isQingLv ? '#d9d2c0' : '#b0c0d0'
    ;(gl as any).setClearColor(clearColor)
    if (scene.fog) {
      ;(scene.fog as THREE.Fog).color.set(clearColor)
    }
    terrainBuffer.smoothed = isPainted
  }, [isQingLv, isShuimo, isPainted, gl, scene])

  return null
}

function Scene({ engine }: { engine: ErosionEngine | null }) {
  const editorMode = useGameStore(s => s.editorMode)
  const toolGroup = useGameStore(s => s.toolGroup)
  const mapStyle = useGameStore(s => s.mapStyle)
  const makerOpen = useGameStore(s => s.makerOpen)
  const makerKind = useGameStore(s => s.makerKind)
  const draft = useGameStore(s => s.draft)
  const plantDraft = useGameStore(s => s.plantDraft)
  const visitMode = useGameStore(s => s.visitMode)
  const isQingLv = mapStyle === 'qinglv'
  const isShuimo = mapStyle === 'shuimo'
  const paperSky = isShuimo ? '#efe8dc' : '#d9d2c0'

  return (
        <>
          {/* 動態場景配色 */}
          <SceneConfig />

          {/* 距離霧 —— 顏色須與天空地平線完全一致 */}
          <fog attach="fog" args={[isQingLv || isShuimo ? paperSky : '#b0c0d0', isShuimo ? 700 : 400, 1400]} />

          {/* 天空 */}
          {isShuimo ? <ProceduralSkyShuimo /> : isQingLv ? <ProceduralSkyQingLv /> : <ProceduralSky />}

          {/* 光照 —— 設色用更柔和暖光，避免樹木過暗 */}
          {isQingLv || isShuimo ? (
            <>
              <ambientLight intensity={0.7} color="#e8ddd0" />
              <directionalLight position={[60, 100, 30]} intensity={1.6} color="#fff0e0" castShadow={false} />
              <hemisphereLight args={['#c8dce8', '#b0a080', 0.45]} />
            </>
          ) : (
            <>
              <ambientLight intensity={0.4} color="#b8c4d0" />
              <directionalLight position={[60, 100, 30]} intensity={1.8} color="#fff8f0" castShadow={false} />
              <hemisphereLight args={['#a8d8ff', '#8B7355', 0.35]} />
            </>
          )}

          {/* 地形 */}
          {isShuimo ? <TerrainMeshShuimo /> : isQingLv ? <TerrainMeshQingLv /> : <TerrainMesh />}
          {isShuimo ? <WaterPlaneShuimo /> : isQingLv ? <WaterPlaneQingLv /> : <WaterPlane />}

          {/* 已放置物件（含使用者手動放置的雲霧） */}
          <PlacedObjects />

          {/* 地圖文字標籤 */}
          <MapLabels />

          <Inhabitants />
          {makerOpen && <DraftPreview species={makerKind === 'plant' ? plantDraft : draft} />}
          <StudioStage />
          <PlaceBeastController />
          <SkyFlock />

          {/* 編輯模式：鍵盤平移（方向鍵 + WASD） */}
          {editorMode === 'edit' && <CameraKeyPan />}

          {/* 地形/物件筆刷（編輯模式 + 對應群組） */}
          {editorMode === 'edit' && !visitMode && (toolGroup === 'terrain' || toolGroup === 'object') && engine && (
            <TerrainEditor engine={engine} />
          )}

          {/* 標籤放置控制器 */}
          {editorMode === 'edit' && !visitMode && toolGroup === 'label' && <EditController />}

          {/* 行走模式 */}
          {editorMode === 'walk' && <FirstPersonController />}
        </>
  )
}

export default function App() {
  const heightmapReady = useGameStore(s => s.heightmapReady)
  const setHeightmapReady = useGameStore(s => s.setHeightmapReady)
  const setEnteredWorld = useGameStore(s => s.setEnteredWorld)
  const isEnteredWorld = useGameStore(s => s.isEnteredWorld)
  const gpuError = useGameStore(s => s.gpuError)
  const setGpuError = useGameStore(s => s.setGpuError)
  const editorMode = useGameStore(s => s.editorMode)
  const setEditorMode = useGameStore(s => s.setEditorMode)
  const toolGroup = useGameStore(s => s.toolGroup)
  const setToolGroup = useGameStore(s => s.setToolGroup)
  const terrainTool = useGameStore(s => s.terrainTool)
  const setTerrainTool = useGameStore(s => s.setTerrainTool)
  const brushRadius = useGameStore(s => s.brushRadius)
  const setBrushRadius = useGameStore(s => s.setBrushRadius)
  const brushStrength = useGameStore(s => s.brushStrength)
  const setBrushStrength = useGameStore(s => s.setBrushStrength)
  const selectedObjectType = useGameStore(s => s.selectedObjectType)
  const setSelectedObjectType = useGameStore(s => s.setSelectedObjectType)
  const mapStyle = useGameStore(s => s.mapStyle)
  const setMapStyle = useGameStore(s => s.setMapStyle)
  const makerOpen = useGameStore(s => s.makerOpen)
  const setMakerOpen = useGameStore(s => s.setMakerOpen)
  const makerKind = useGameStore(s => s.makerKind)
  const setMakerKind = useGameStore(s => s.setMakerKind)
  const visitMode = useGameStore(s => s.visitMode)
  const setVisitMode = useGameStore(s => s.setVisitMode)
  const encounterToast = useGameStore(s => s.encounterToast)
  const setEncounterToast = useGameStore(s => s.setEncounterToast)
  const placingBeast = useGameStore(s => s.placingBeast)
  const sightings = useGameStore(s => s.sightings)
  const worldId = useGameStore(s => s.worldId)

  const [engine, setEngine] = useState<ErosionEngine | null>(null)
  const [webGpuReady, setWebGpuReady] = useState(false)
  const [webGpuChecked, setWebGpuChecked] = useState(false)
  const [walkTransition, setWalkTransition] = useState(false)
  const [submenuOpen, setSubmenuOpen] = useState(true)
  const [showVisits, setShowVisits] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const visitEntered = useRef(false)

  useWorldPersist(engine, heightmapReady)

  useEffect(() => {
    const q = new URLSearchParams(location.search)
    if (q.get('visit') === '1') setVisitMode(true)
  }, [setVisitMode])

  useEffect(() => {
    if (!encounterToast) return
    const t = window.setTimeout(() => setEncounterToast(null), 3200)
    return () => window.clearTimeout(t)
  }, [encounterToast, setEncounterToast])

  /** 物件類型列表 */
  const objectTypes: { key: PlacedObjectType; label: string }[] = [
    { key: 'rock', label: '岩石' },
    { key: 'bush', label: '點苔' },
  ]

  /** 樹木子類型 */
  const treeTypes: { key: PlacedObjectType; label: string }[] = [
    { key: 'tree_pine', label: '松樹' },
    { key: 'tree_broad', label: '闊葉' },
    { key: 'tree_willow', label: '柳樹' },
    { key: 'tree_hanlin', label: '寒林' },
    { key: 'tree_aspen', label: '白楊' },
    { key: 'tree_ash', label: '白蠟' },
    { key: 'tree_crabclaw', label: '蟹爪' },
    { key: 'tree_broad_dot', label: '群叢' },
    { key: 'tree_bush', label: '灌叢' },
  ]

  // 判斷當前工具屬於哪個一級選單
  const isShanshui = toolGroup === 'terrain' || (toolGroup === 'object' && selectedObjectType === 'rock')
  const isCaomu = toolGroup === 'object' && selectedObjectType !== 'rock'

  /** 地形工具列表 */
  const terrainTools: { key: TerrainTool; label: string; icon: string; hint: string }[] = [
    { key: 'raise', icon: '山', label: '堆山', hint: '左鍵抬高地形 · Shift 降低' },
    { key: 'dig', icon: '掘', label: '挖掘', hint: '左鍵降低地形 · Shift 抬高' },
    { key: 'flatten', icon: '平', label: '平整', hint: '左鍵將周圍地形拉齊到點擊處高度' },
    { key: 'smooth', icon: '滑', label: '平滑', hint: '左鍵撫平起伏，讓地形更圓潤' },
    { key: 'roughen', icon: '粗', label: '粗糙', hint: '左鍵添加自然不規則起伏 · Shift 反向' },
  ]

  // WebGPU 檢查
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!navigator.gpu) {
        if (!cancelled) { setGpuError('此瀏覽器不支援 WebGPU'); setWebGpuChecked(true) }
        return
      }
      try {
        const adapter = await navigator.gpu.requestAdapter()
        if (!adapter) {
          if (!cancelled) { setGpuError('找不到 GPU 適配器'); setWebGpuChecked(true) }
          return
        }
        if (!cancelled) {
          setGpuError(null)
          setWebGpuReady(true)
          setWebGpuChecked(true)
        }
      } catch {
        if (!cancelled) { setGpuError('GPU 初始化失敗'); setWebGpuChecked(true) }
      }
    })()
    return () => { cancelled = true }
  }, [setGpuError])

  // 建立並啟動離屏侵蝕引擎
  useEffect(() => {
    if (!webGpuReady) return

    const e = new ErosionEngine(HEIGHTMAP_SIZE)

    e.onReady(() => {
      setHeightmapReady(true)
    })

    e.onError((msg) => {
      setGpuError(msg)
    })

    e.start()
    setEngine(e)

    return () => {
      e.dispose()
      setEngine(null)
    }
  }, [webGpuReady, setHeightmapReady])

  // 鍵盤快捷鍵：1-5 切換工具，[ ] 調整筆刷
  useEffect(() => {
    if (editorMode !== 'edit') return
    const toolKeys: TerrainTool[] = ['raise', 'dig', 'flatten', 'smooth', 'roughen']
    const onKeyDown = (e: KeyboardEvent) => {
      const n = parseInt(e.key)
      if (n >= 1 && n <= 5 && toolGroup === 'terrain') {
        setTerrainTool(toolKeys[n - 1])
      }
      if (e.key === '[') setBrushRadius(Math.max(0.005, brushRadius - 0.005))
      if (e.key === ']') setBrushRadius(Math.min(0.15, brushRadius + 0.005))
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [editorMode, toolGroup, brushRadius, setTerrainTool, setBrushRadius])

  // 進入行走模式（平滑過渡）
  const enterWalk = useCallback(() => {
    setWalkTransition(true)
    // 等過渡動畫播完後再切換模式並自動進入世界
    setTimeout(() => {
      setEditorMode('walk')
      setEnteredWorld(false)
      // 觸發自動進入
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('shj-enter-world'))
        setWalkTransition(false)
      }, 100)
    }, 800)
  }, [setEditorMode, setEnteredWorld])

  useEffect(() => {
    if (!visitMode || !heightmapReady || visitEntered.current) return
    if (useGameStore.getState().editorMode === 'walk') return
    visitEntered.current = true
    enterWalk()
  }, [visitMode, heightmapReady, enterWalk])

  const copyVisitLink = useCallback(() => {
    const url = new URL(location.href)
    url.searchParams.set('visit', '1')
    void navigator.clipboard.writeText(url.toString()).then(() => {
      setEncounterToast('已複製訪客連結（同一瀏覽器可開）')
    })
  }, [setEncounterToast])

  const exportWorld = useCallback(() => {
    downloadWorldPack(collectSave(useGameStore.getState().worldId))
    setEncounterToast('已匯出島檔')
  }, [setEncounterToast])

  const importWorld = useCallback(async (file: File) => {
    const save = await parseWorldPack(file)
    hydrateWorldSave(save)
    if (engine) engine.restoreHeightmap(new Float32Array(save.heightmap))
    await writeWorldSave(save)
    setEncounterToast('已載入島檔')
  }, [engine, setEncounterToast])

  // 選取工具的 helper
  const selectTerrainTool = useCallback((tool: TerrainTool) => {
    setToolGroup('terrain')
    setTerrainTool(tool)
    // 挖掘工具預設小筆刷（適合挖河流湖泊），其餘正常
    setBrushRadius(tool === 'dig' ? 0.03 : 0.05)
    setBrushStrength(0.5)
  }, [setToolGroup, setTerrainTool, setBrushRadius, setBrushStrength])

  const selectObjectType = useCallback((type: PlacedObjectType) => {
    setToolGroup('object')
    setSelectedObjectType(type)
    // 預設單點模式（筆刷最小值）
    setBrushRadius(0.008)
    setBrushStrength(0.5)
  }, [setToolGroup, setSelectedObjectType, setBrushRadius, setBrushStrength])

  // WebGPU 未就緒
  if (!webGpuChecked) {
    return (
      <div className="loading-screen">
        正在檢查 GPU…
      </div>
    )
  }

  if (gpuError) {
    return (
      <div className="loading-screen">
        <p>{gpuError}</p>
        <p style={{ fontSize: '0.8rem', opacity: 0.6 }}>我的山海需要 WebGPU 支援的瀏覽器</p>
      </div>
    )
  }

  // 是否為單點模式（筆刷極小 → worldRadius < 8）
  const isSinglePoint = toolGroup !== 'terrain' && toolGroup !== 'label' && brushRadius < 0.014

  // 目前活躍工具的提示文字
  const activeHint = placingBeast
    ? '點地面投放 · Shift 收回'
    : toolGroup === 'beast'
      ? '作坊裡換件、捏形，再投放山上'
      : toolGroup === 'plant'
        ? '選底，調葉色／花／果／光，再種上山'
      : toolGroup === 'terrain'
        ? terrainTools.find(t => t.key === terrainTool)?.hint ?? ''
        : toolGroup === 'label'
            ? '左鍵放置標籤 · 雙擊編輯 · 拖曳移動 · Shift+左鍵刪除'
            : isSinglePoint ? '單點放置 · Shift+左鍵刪除' : '左鍵拖曳放置 · Shift+左鍵擦除'

  // 筆刷參數範圍（依工具群組不同）
  const brushConfig = toolGroup === 'terrain'
    ? { minR: 0.005, maxR: 0.15, stepR: 0.003 }
    : { minR: 0.008, maxR: 0.035, stepR: 0.001 } // object

  return (
    <>
      {/* ── Three.js/WebGPU Canvas ── */}
      <Canvas
        camera={{ fov: 70, near: 0.1, far: 2000, position: [0, 350, 300] }}
        gl={(canvas) => {
          const renderer = new WebGPURenderer({
            ...(canvas as any),
            powerPreference: 'high-performance',
            antialias: true,
            alpha: false,
          })
          renderer.setClearColor('#b0c0d0')
          renderer.toneMapping = THREE.ACESFilmicToneMapping
          renderer.toneMappingExposure = 1.1
          return renderer.init().then(() => renderer)
        }}
        dpr={[1, 1.5]}
      >
        <Suspense fallback={null}>
          {heightmapReady && <Scene engine={engine} />}
        </Suspense>
      </Canvas>

      {/* ── 等待侵蝕引擎初始化 ── */}
      {!heightmapReady && (
        <div className="loading-screen">
          正在開闢天地…
        </div>
      )}

      {/* ── 「遊」按鈕 — 固定右上角 ── */}
      {((editorMode === 'edit') || visitMode) && heightmapReady && editorMode !== 'walk' && (
        <button type="button" className="btn-you" onClick={enterWalk} title="進入山海世界">
          遊
        </button>
      )}

      {heightmapReady && !visitMode && editorMode === 'edit' && (
        <div className="beast-dock">
          <button
            type="button"
            className={`btn-tool btn-primary-menu ${makerOpen && makerKind === 'beast' ? 'active' : ''}`}
            onClick={() => {
              if (makerOpen && makerKind === 'beast') setMakerOpen(false)
              else { setMakerKind('beast'); setMakerOpen(true); setToolGroup('beast') }
            }}
          >
            <span className="tool-icon-cn">神獸</span>
          </button>
          <button
            type="button"
            className={`btn-tool btn-primary-menu ${makerOpen && makerKind === 'plant' ? 'active' : ''}`}
            onClick={() => {
              if (makerOpen && makerKind === 'plant') setMakerOpen(false)
              else { setMakerKind('plant'); setMakerOpen(true); setToolGroup('plant') }
            }}
          >
            <span className="tool-icon-cn">奇樹</span>
          </button>
          <button type="button" className="btn-ghost" onClick={copyVisitLink}>請人來</button>
          <button type="button" className="btn-ghost" onClick={exportWorld}>匯出</button>
          <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()}>載入</button>
          <button type="button" className="btn-ghost" onClick={() => setShowVisits(v => !v)}>
            來訪 {sightings.length}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            hidden
            onChange={e => {
              const f = e.target.files?.[0]
              if (f) void importWorld(f)
              e.target.value = ''
            }}
          />
        </div>
      )}

      {visitMode && heightmapReady && (
        <div className="visit-banner">訪客 · 可走、可遇見，不能改山</div>
      )}

      {makerOpen && makerKind === 'beast' && <BeastStudio />}
      {makerOpen && makerKind === 'plant' && <PlantMaker />}

      {encounterToast && <div className="encounter-toast">{encounterToast}</div>}

      {showVisits && !visitMode && (
        <div className="visit-log">
          <div className="beast-maker-head">
            <span>來訪 · {worldId.slice(0, 8)}</span>
            <button type="button" className="panel-close" onClick={() => setShowVisits(false)}>×</button>
          </div>
          {sightings.length === 0 && <p className="beast-hint">尚無人遇見。</p>}
          {sightings.slice(-12).reverse().map((sg, i) => (
            <p key={`${sg.at}-${i}`} className="beast-hint">
              {sg.at.slice(11, 16)} 見過 {useGameStore.getState().species.find(s => s.id === sg.speciesId)?.name ?? sg.speciesId}
            </p>
          ))}
        </div>
      )}

      {/* ── 底部工具欄 — 一級選單：丹青 | 山水 / 草木 / 題記 ── */}
      {editorMode === 'edit' && heightmapReady && !visitMode && (
        <div className="toolbar">
          {/* 丹青（風格切換）— 左側獨立 */}
          <div className="toolbar-danqing">
            <button
              type="button"
              className={`btn-tool btn-primary-menu ${mapStyle !== 'realistic' ? 'active' : ''}`}
              onClick={() => setMapStyle(mapStyle === 'qinglv' ? 'shuimo' : mapStyle === 'shuimo' ? 'realistic' : 'qinglv')}
              title={mapStyle === 'shuimo' ? '水墨' : mapStyle === 'qinglv' ? '青綠' : '寫實'}
            >
              <span className="tool-icon-cn">丹青</span>
            </button>
          </div>

          {/* 山水（地形 + 岩石） */}
          <div className="toolbar-group">
            <button
              type="button"
              className={`btn-tool btn-primary-menu ${isShanshui ? 'active shanshui' : ''}`}
              onClick={() => {
                if (isShanshui) { setSubmenuOpen(o => !o) }
                else { selectTerrainTool('raise'); setSubmenuOpen(true) }
              }}
            >
              <span className="tool-icon-cn">山水</span>
            </button>
          </div>

          <div className="toolbar-sep" />

          {/* 草木（樹木 + 點苔 + 雲霧） */}
          <div className="toolbar-group">
            <button
              type="button"
              className={`btn-tool btn-primary-menu ${isCaomu ? 'active veg' : ''}`}
              onClick={() => {
                if (isCaomu) { setSubmenuOpen(o => !o) }
                else {
                  selectObjectType('tree_pine')
                  setSubmenuOpen(true)
                }
              }}
            >
              <span className="tool-icon-cn">草木</span>
            </button>
          </div>

          <div className="toolbar-sep" />

          {/* 題記（標籤） */}
          <div className="toolbar-group">
            <button
              type="button"
              className={`btn-tool btn-primary-menu ${toolGroup === 'label' ? 'active' : ''}`}
              onClick={() => setToolGroup('label')}
              title="左鍵放置文字標籤 · 雙擊編輯 · Shift+左鍵刪除"
            >
              <span className="tool-icon-cn">題記</span>
            </button>
          </div>

          {/* 筆刷參數（右側） */}
          {toolGroup !== 'label' && toolGroup !== 'beast' && toolGroup !== 'plant' && (
            <div className="toolbar-brush">
              {isSinglePoint && <span className="single-point-badge">單點</span>}
              <label className="slider-row">
                <span>大小</span>
                <input
                  type="range"
                  min={brushConfig.minR}
                  max={brushConfig.maxR}
                  step={brushConfig.stepR}
                  value={brushRadius}
                  onChange={e => setBrushRadius(parseFloat(e.target.value))}
                />
              </label>
              <label className="slider-row">
                <span>力度</span>
                <input
                  type="range"
                  min="0.05"
                  max="1.0"
                  step="0.05"
                  value={brushStrength}
                  onChange={e => setBrushStrength(parseFloat(e.target.value))}
                />
              </label>
            </div>
          )}
        </div>
      )}

      {/* ── 二級選單：山水 ── */}
      {editorMode === 'edit' && heightmapReady && !visitMode && isShanshui && submenuOpen && (
        <div className="toolbar-submenu">
          {terrainTools.map(t => (
            <button
              key={t.key}
              type="button"
              className={`btn-submenu-item ${toolGroup === 'terrain' && terrainTool === t.key ? 'active' : ''}`}
              onClick={() => selectTerrainTool(t.key)}
              title={t.hint}
            >
              {t.label}
            </button>
          ))}
          <span className="submenu-sep" />
          <button
            type="button"
            className={`btn-submenu-item ${toolGroup === 'object' && selectedObjectType === 'rock' ? 'active obj' : ''}`}
            onClick={() => selectObjectType('rock')}
          >
            岩石
          </button>
        </div>
      )}

      {/* ── 二級選單：草木 ── */}
      {editorMode === 'edit' && heightmapReady && !visitMode && isCaomu && submenuOpen && (
        <div className="toolbar-submenu">
          {treeTypes.map(t => (
            <button
              key={t.key}
              type="button"
              className={`btn-submenu-item ${toolGroup === 'object' && selectedObjectType === t.key ? 'active obj' : ''}`}
              onClick={() => selectObjectType(t.key)}
            >
              {t.label}
            </button>
          ))}
          <span className="submenu-sep" />
          <button
            type="button"
            className={`btn-submenu-item ${toolGroup === 'object' && selectedObjectType === 'bush' ? 'active obj' : ''}`}
            onClick={() => selectObjectType('bush')}
          >
            點苔
          </button>
          <span className="submenu-sep" />
          <button
            type="button"
            className={`btn-submenu-item ${toolGroup === 'object' && selectedObjectType === 'cloud_fog' ? 'active obj' : ''}`}
            onClick={() => selectObjectType('cloud_fog')}
          >
            雲霧
          </button>
        </div>
      )}

      {/* ── 提示文字 ── */}
      {editorMode === 'edit' && heightmapReady && !visitMode && (
        <div className={`toolbar-hint${(isShanshui || isCaomu) && submenuOpen ? ' submenu-open' : ''}`}>
          {activeHint}
          <span className="toolbar-hint-sub"> · 右鍵旋轉 · 中鍵平移 · 滾輪縮放 · [ ] 筆刷大小</span>
        </div>
      )}

      {/* ── 過渡動畫：水墨暈染過渡 ── */}
      {walkTransition && (
        <div className="walk-transition">
          <div className="walk-transition-text">入 山 海</div>
        </div>
      )}

      {/* ── 行走模式：HUD ── */}
      {editorMode === 'walk' && isEnteredWorld && (
        <>
          <div className="crosshair" />
          <div className="hud">
            {visitMode
              ? 'WASD 移動 · Shift 奔跑 · 走近神獸可遇見'
              : 'WASD 移動 · Shift 奔跑 · Space 跳躍 · ESC 返回編輯'}
          </div>
        </>
      )}
    </>
  )
}
